import { useBunja } from 'bunja/react';
import { format, intervalToDuration } from 'date-fns';
import { isEmpty } from 'es-toolkit/compat';
import { useAtomValue, useSetAtom } from 'jotai';

import { Notification } from '@flipster/apis/v1/user/types';
import { showErrorDialog } from '@flipster/features/src/shared/show-error-dialog';
import { StatusBadge } from '@flipster/react/design-system/status-badge';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import { css, sva } from '@flipster/styled-system/css';
import { center, hstack, vstack } from '@flipster/styled-system/patterns';
import { convertDate } from '@flipster/utils/datetime';
import segment from '@flipster/utils/segment';
import { NotificationCenterEvent } from '@flipster/utils/segment/events/notification-center';

import { notificationCenterBunja } from './bunja';

const notificationItemRecipe = sva({
  slots: ['container', 'groupName', 'title', 'body', 'time'],
  base: {
    container: {
      py: 16,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderColor: 'border.default.level-1',
    },
    groupName: {
      textStyle: 'Text/XS/Strong',
      pb: 2,
    },
    title: {
      textStyle: 'Text/L/Strong',
      pb: 8,
    },
    body: {
      textStyle: 'Text/M/Regular',
      pb: 8,
    },
    time: {
      textStyle: 'Text/S/Regular',
      color: 'content.default.level-1',
    },
  },
  variants: {
    isRead: {
      true: {
        title: { color: 'content.default.level-1' },
        body: { color: 'content.default.level-1' },
      },
      false: {
        title: { color: 'content.default.level-5' },
        body: { color: 'content.default.level-3' },
      },
    },
  },
});

type NotificationItemProps = Notification & {
  listOrder: number;
};

export const NotificationItem = ({
  id,
  createdAt,
  title,
  body,
  isRead,
  groupColor,
  groupName,
  path,
  listOrder,
}: NotificationItemProps) => {
  const classes = notificationItemRecipe({ isRead });
  const { isNotificationCenterOpenAtom, markNotificationAsReadMutationAtom } = useBunja(notificationCenterBunja);
  const setIsOpen = useSetAtom(isNotificationCenterOpenAtom);
  const { mutateAsync } = useAtomValue(markNotificationAsReadMutationAtom);

  const { timeString, timeType } = formatTimeWithType(createdAt);

  const handleClick = async () => {
    try {
      if (!isRead) {
        await mutateAsync(id);
      }

      segment.track(NotificationCenterEvent.NotiCenterNotiClick, {
        noti_id: id,
        title,
        body,
        is_read: isRead,
        noti_page: groupName,
        noti_time: createdAt,
        time_type: timeType,
        list_order: listOrder,
      });
      setIsOpen(false);
    } catch (error) {
      showErrorDialog(error);
    }
  };

  return (
    <IsomorphicLink
      href={isEmpty(path) ? '/available-on-app' : path}
      className={css({
        display: 'block',
        color: 'inherit',
        _hover: {
          opacity: 0.8,
        },
      })}
      onClick={handleClick}
    >
      <div className={classes.container}>
        <div className={hstack({ gap: '8px', alignItems: 'flex-start' })}>
          <div className={center({ h: 12 })}>
            <StatusBadge visible={!isRead} color='red' />
          </div>
          <div className={vstack({ alignItems: 'flex-start', flex: '1' })}>
            <p style={{ color: groupColor }} className={classes.groupName}>
              {groupName}
            </p>
            <p className={classes.title}>{title}</p>
            <p className={classes.body}>{body}</p>
            <p className={classes.time}>{timeString}</p>
          </div>
        </div>
      </div>
    </IsomorphicLink>
  );
};
function formatTimeWithType(dateInput: string | Date): { timeString: string; timeType: string } {
  const date = convertDate(dateInput);
  if (!date) return { timeString: '', timeType: 'date' };

  const now = new Date();
  const duration = intervalToDuration({ start: date, end: now });

  if (duration.years) {
    return {
      timeString: format(date, 'yyyy MMM dd, hh:mm a'),
      timeType: 'year',
    };
  }

  if (duration.months || duration.days) {
    return {
      timeString: format(date, 'MMM dd, hh:mm a'),
      timeType: 'date',
    };
  }

  if (duration.hours) {
    return {
      timeString: `${duration.hours} hour${duration.hours === 1 ? '' : 's'} ago`,
      timeType: 'hour',
    };
  }

  if (duration.minutes) {
    return {
      timeString: `${duration.minutes} minute${duration.minutes === 1 ? '' : 's'} ago`,
      timeType: 'minute',
    };
  }

  return {
    timeString: 'Just now',
    timeType: 'now',
  };
}
